
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelRoleBadge } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelDescriptionClass,
  painelTableShellClass,
} from '@/components/painel/painel-styles';
import {
  PAINEL_ROLE_LABELS,
  canDeletePainelUserTarget,
  canManagePainelUserTarget,
  type PainelMockUser,
} from '@/lib/painel-rbac';
import {
  createPainelUser,
  deletePainelUser,
  getPainelUsers,
  savePainelUserPatch,
  type PainelUserEditableFields,
} from '@/lib/painel-users-store';
import { cn } from '@/lib/utils';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { PainelUserAvatar } from '@/components/painel/PainelUserAvatar';
import { PainelUserEditorModal } from '@/components/painel/PainelUserEditorModal';
import { PainelUserCreateModal } from '@/components/painel/PainelUserCreateModal';
import { PainelUserProfileSheet } from '@/components/painel/PainelUserProfileSheet';

function StatusBadge({ status }: { status: PainelMockUser['status'] }) {
  const active = status === 'active';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 font-inter text-[11px] font-semibold',
        active ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25' : 'bg-slate-500/15 text-slate-400 ring-1 ring-white/10'
      )}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

export function PainelUsuariosPage() {
  const pageText = usePainelText('usuarios');
  const { role, session } = usePainelSession();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const canManage = role === 'super_admin';
  const actorUsername = session?.username;

  const [users, setUsers] = useState<PainelMockUser[]>([]);
  const [selected, setSelected] = useState<PainelMockUser | null>(null);
  const [editing, setEditing] = useState<PainelMockUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PainelMockUser | null>(null);
  const [listAnimating, setListAnimating] = useState(true);

  useEffect(() => {
    void getPainelUsers().then(setUsers);
  }, []);

  useEffect(() => {
    if (!session?.username) return;
    setUsers((prev) =>
      prev.map((user) =>
        user.username === session.username
          ? { ...user, avatarUrl: session.avatarUrl ?? user.avatarUrl }
          : user
      )
    );
    setSelected((prev) =>
      prev && prev.username === session.username
        ? { ...prev, avatarUrl: session.avatarUrl ?? prev.avatarUrl }
        : prev
    );
  }, [session?.username, session?.avatarUrl]);

  useEffect(() => {
    setListAnimating(true);
    const ms = 220 + users.length * 35;
    const timer = window.setTimeout(() => setListAnimating(false), ms);
    return () => window.clearTimeout(timer);
  }, [users.length]);

  const [profileRefreshKey, setProfileRefreshKey] = useState(0);

  const openProfile = (user: PainelMockUser) => {
    setSelected(user);
    setProfileRefreshKey((key) => key + 1);
  };

  const openEditor = (user: PainelMockUser) => {
    if (!canManagePainelUserTarget(actorUsername, user)) return;
    setEditing(user);
  };

  const handleSave = async (id: string, patch: PainelUserEditableFields) => {
    const target = users.find((u) => u.id === id);
    const updated = await savePainelUserPatch(id, patch, {
      targetUsername: target?.username,
      actorUsername,
    });
    setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
    setSelected((prev) => (prev && prev.id === id ? updated : prev));
    showSaveSuccess({
      title: 'Usuário atualizado',
      description: `${updated.name} agora tem o cargo ${PAINEL_ROLE_LABELS[updated.role]}.`,
    });
  };

  const handleDelete = (id: string) => {
    const target = users.find((u) => u.id === id) ?? deleting;
    if (!canDeletePainelUserTarget(actorUsername, target)) return;
    void deletePainelUser(id, { targetUsername: target?.username }).then(() => {
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setSelected((prev) => (prev && prev.id === id ? null : prev));
      setEditing((prev) => (prev && prev.id === id ? null : prev));
      setDeleting(null);
    });
  };

  const handleCreate = (fields: Parameters<typeof createPainelUser>[0]) => {
    void createPainelUser(fields).then((created) => {
      setUsers((prev) => [...prev, created]);
      showSaveSuccess({
        title: 'Usuário adicionado',
        description: `${fields.name} foi criado com o cargo ${PAINEL_ROLE_LABELS[fields.role]}.`,
      });
      setCreating(false);
    });
    return true;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            Management
          </p>
          <h1 className="mt-1 font-poppins text-2xl font-bold text-white md:text-3xl">{pageText.title}</h1>
          <p className={cn('mt-2 max-w-2xl text-sm', painelDescriptionClass)}>{pageText.description}</p>
        </div>
        {canManage ? (
          <Button
            type="button"
            className={cn(painelButtonPrimaryClass, 'h-10 shrink-0 shadow-lg shadow-sky-950/30')}
            onClick={() => setCreating(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar usuário
          </Button>
        ) : null}
      </div>

      <div className={cn(painelTableShellClass, listAnimating && 'overflow-hidden')}>
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="w-full min-w-[720px] text-left font-inter text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03] text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3 font-semibold">Avatar</th>
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">Cargo</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Último acesso</th>
                <th className="px-4 py-3 font-semibold">Criado em</th>
                <th className="px-4 py-3 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const canEditTarget = canManage && canManagePainelUserTarget(actorUsername, user);
                const canDeleteTarget =
                  canManage && canDeletePainelUserTarget(actorUsername, user);

                return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.22, delay: index * 0.035, ease: 'easeOut' }}
                    className="border-b border-white/5 transition-colors hover:bg-white/[0.04]"
                  >
                    <td className="px-4 py-3">
                      <PainelUserAvatar
                        name={user.name}
                        avatarUrl={user.avatarUrl}
                        className="rounded-xl"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openProfile(user)}
                        className="font-medium text-blue-50 transition-colors hover:text-sky-300"
                      >
                        {user.name}
                      </button>
                      <p className="text-xs text-slate-500">@{user.username}</p>
                    </td>
                    <td className="px-4 py-3">
                      <PainelRoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(user.lastLoginAt)}</td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-3 text-slate-400 hover:bg-white/10 hover:text-white"
                          onClick={() => openProfile(user)}
                        >
                          Ver
                        </Button>
                        {canEditTarget ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:bg-white/10 hover:text-white"
                            onClick={() => openEditor(user)}
                            aria-label={`Editar ${user.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {canDeleteTarget ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:bg-rose-500/15 hover:text-rose-300"
                            onClick={() => setDeleting(user)}
                            aria-label={`Excluir ${user.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PainelUserProfileSheet
        key={profileRefreshKey}
        user={selected}
        open={Boolean(selected)}
        onOpenChange={(open) => !open && setSelected(null)}
        viewerRole={role}
        canManage={
          Boolean(selected) &&
          canManage &&
          canManagePainelUserTarget(actorUsername, selected)
        }
        canDelete={
          Boolean(selected) &&
          canManage &&
          canDeletePainelUserTarget(actorUsername, selected)
        }
        onEdit={openEditor}
        onDelete={setDeleting}
      />

      {canManage ? (
        <>
          <PainelUserCreateModal
            open={creating}
            onOpenChange={setCreating}
            onCreate={handleCreate}
          />

          <PainelUserEditorModal
            user={editing}
            open={Boolean(editing)}
            onOpenChange={(open) => !open && setEditing(null)}
            onSave={handleSave}
            canChangePassword={canManage}
          />

          <PainelConfirmDialog
            open={deleting !== null}
            onOpenChange={(o) => !o && setDeleting(null)}
            title="Excluir usuário?"
            description={
              deleting
                ? `“${deleting.name}” será removido do painel. Esta ação não pode ser anulada.`
                : 'Esta ação não pode ser anulada.'
            }
            confirmLabel="Excluir"
            onConfirm={() => {
              if (deleting) handleDelete(deleting.id);
            }}
          />
        </>
      ) : null}
    </div>
  );
}
